import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '@/lib/auth-context';
import { uploadAsset, getProjectAssets, deleteAsset } from '@/lib/assets-service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/lib/use-toast';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { UploadCloud, XCircle, FileAudio, FileImage, File, Share2, Play, Pause, Trash2 } from 'lucide-react';

const ACCEPTED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'audio/mpeg', 'audio/wav', 'audio/ogg'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const AssetManager = ({ projectId, workdir, isSimplified = false }) => {
  const { currentUser } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [assets, setAssets] = useState([]);
  const [sharedAssets, setSharedAssets] = useState([]);
  const [assetToDelete, setAssetToDelete] = useState(null);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  // Charger les assets au montage et quand le projectId change
  useEffect(() => {
    if (isSimplified && workdir) {
      // En mode simplifié, charger les assets depuis le workdir
      loadCourseAssets();
    } else if (currentUser && projectId) {
      // Mode normal, charger les assets du projet utilisateur
      loadAssets();
      // Ne charger les assets partagés que si ce n'est pas en mode simplifié
      if (!isSimplified) {
        loadSharedAssets();
      }
    }
  }, [currentUser, projectId, workdir, isSimplified]);

  // Gestionnaires d'événements pour le drag & drop - désactivés en mode simplifié
  useEffect(() => {
    if (isSimplified) return; // Pas de drag & drop en mode simplifié

    let dragCounter = 0;

    const handleDragEnter = (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter++;
      setIsDragging(true);
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter--;
      if (dragCounter === 0) {
        setIsDragging(false);
      }
    };

    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter = 0;
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        processFile(files[0]);
      }
    };

    const dropZone = dropZoneRef.current;
    if (dropZone) {
      dropZone.addEventListener('dragenter', handleDragEnter);
      dropZone.addEventListener('dragleave', handleDragLeave);
      dropZone.addEventListener('dragover', handleDragOver);
      dropZone.addEventListener('drop', handleDrop);
    }

    return () => {
      if (dropZone) {
        dropZone.removeEventListener('dragenter', handleDragEnter);
        dropZone.removeEventListener('dragleave', handleDragLeave);
        dropZone.removeEventListener('dragover', handleDragOver);
        dropZone.removeEventListener('drop', handleDrop);
      }
    };
  }, [isSimplified]);

  const loadAssets = async () => {
    setLoading(true);
    try {
      const projectAssets = await getProjectAssets(currentUser.uid, projectId);
      setAssets(projectAssets);
    } catch (error) {
      console.error("Erreur lors du chargement des assets:", error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les assets.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadSharedAssets = async () => {
    try {
      // Utiliser une API simple pour récupérer les assets partagés
      const response = await fetch('/api/list-assets?shared=true');
      if (response.ok) {
        const sharedAssetsList = await response.json();
        setSharedAssets(sharedAssetsList);
      }
    } catch (error) {
      console.error("Erreur lors du chargement des assets partagés:", error);
    }
  };

  const loadCourseAssets = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/list-assets?workdir=${encodeURIComponent(workdir)}`);
      if (response.ok) {
        const courseAssets = await response.json();
        setAssets(courseAssets);
      } else {
        console.error("Erreur lors du chargement des assets du cours");
        setAssets([]);
      }
    } catch (error) {
      console.error("Erreur lors du chargement des assets du cours:", error);
      setAssets([]);
    } finally {
      setLoading(false);
    }
  };

  const processFile = (file) => {
    if (!file) return;
    
    // Vérifier la taille du fichier
    if (file.size > MAX_FILE_SIZE) {
      toast({
        title: "Fichier trop volumineux",
        description: "La taille du fichier ne doit pas dépasser 5MB.",
        variant: "destructive",
      });
      return;
    }
    
    // Vérifier le type de fichier
    if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
      toast({
        title: "Type de fichier non supporté",
        description: "Formats acceptés: JPEG, PNG, GIF, MP3, WAV, OGG",
        variant: "destructive",
      });
      return;
    }
    
    // Déterminer le type d'asset
    const type = file.type.startsWith('image/') ? 'image' : 'sound';
    
    setLoading(true);
    uploadAsset(currentUser.uid, projectId, file, type)
      .then(() => loadAssets())
      .then(() => {
        toast({
          title: "Succès",
          description: "Asset téléchargé avec succès.",
          variant: "success",
        });
      })
      .catch(error => {
        console.error("Erreur lors du téléchargement:", error);
        toast({
          title: "Erreur",
          description: `Impossible de télécharger l'asset: ${error.message}`,
          variant: "destructive",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    processFile(file);
    // Réinitialiser l'input file
    event.target.value = null;
  };

  const handleDeleteAsset = async () => {
    if (!assetToDelete) return;
    
    setLoading(true);
    try {
      await deleteAsset(assetToDelete.path);
      // Mettre à jour la liste des assets
      await loadAssets();
      toast({
        title: "Succès",
        description: "Asset supprimé avec succès.",
        variant: "success",
      });
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      toast({
        title: "Erreur",
        description: `Impossible de supprimer l'asset: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setAssetToDelete(null);
      setShowDeleteAlert(false);
    }
  };

  // Instruction d'utilisation pour les assets dans le code
  const getCodeExample = (asset) => {
    // Extraire juste le nom du fichier du chemin complet
    const fileName = asset.name;
    
    if (asset.type === 'image') {
      return `loadImage('${fileName}')`;
    } else if (asset.type === 'sound') {
      return `loadSound('${fileName}')`;
    }
    return '';
  };

  // Afficher une icône selon le type de fichier
  const getAssetIcon = (asset) => {
    if (asset.type === 'image') {
      return <FileImage className="h-4 w-4" />;
    } else if (asset.type === 'sound') {
      return <FileAudio className="h-4 w-4" />;
    }
    return <File className="h-4 w-4" />;
  };

  // Composant audio personnalisé avec juste play/pause
  const CustomAudioPlayer = ({ src }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const audioRef = useRef(null);

    const togglePlay = () => {
      if (audioRef.current) {
        if (isPlaying) {
          audioRef.current.pause();
        } else {
          audioRef.current.play();
        }
        setIsPlaying(!isPlaying);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
    };

    return (
      <div className="flex items-center justify-center">
        <audio
          ref={audioRef}
          src={src}
          onEnded={handleEnded}
          className="hidden"
        />
        <button
          onClick={togglePlay}
          className="flex items-center justify-center w-8 h-8 rounded bg-gray-800 hover:bg-gray-700 text-white transition-colors"
        >
          {isPlaying ? (
            <Pause className="w-4 h-4" />
          ) : (
            <Play className="w-4 h-4 ml-0.5" />
          )}
        </button>
      </div>
    );
  };

  // Rendre un asset (utilisé pour les assets du projet et les assets partagés)
  const renderAsset = (asset, isShared = false) => {
    return (
      <div key={asset.id} className={`relative group ${isShared ? 'opacity-80' : ''}`}>
        {/* Cadre principal pour l'asset */}
        <div className="w-full aspect-square flex items-center justify-center overflow-hidden border rounded-md">
          {asset.type === 'image' ? (
            <img 
              src={asset.url} 
              alt={asset.name}
              className="max-w-full max-h-full object-contain"
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center p-2">
              <FileAudio className="h-6 w-6 mb-2 text-gray-500" />
              <CustomAudioPlayer src={asset.url} />
            </div>
          )}
        </div>
        
        {/* Bouton de suppression en haut à droite */}
        {!isShared && !isSimplified && (
          <button
            onClick={() => {
              setAssetToDelete(asset);
              setShowDeleteAlert(true);
            }}
            className="absolute top-1 right-1 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
            title="Supprimer"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
        
        {/* Nom et code en dessous */}
        <div className="mt-1 flex flex-col">
          <div className="flex items-center gap-1">
            <p className="text-xs truncate">{asset.name}</p>
          </div>
          <code className="text-xs bg-gray-100 dark:bg-gray-800 p-1 rounded mt-1 cursor-pointer text-center overflow-hidden text-ellipsis whitespace-nowrap" 
            onClick={() => {
              navigator.clipboard.writeText(getCodeExample(asset));
              toast({
                title: "Copié !",
                description: "Code copié dans le presse-papier.",
                variant: "success",
              });
            }}
          >
            {getCodeExample(asset)}
          </code>
        </div>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col">
      {/* Bouton d'upload - masqué en mode simplifié */}
      {!isSimplified && (
        <div className="flex justify-between items-center mb-4">
          <div></div>
          <input
            type="file"
            ref={fileInputRef}
            accept={ACCEPTED_FILE_TYPES.join(',')}
            onChange={handleFileUpload}
            disabled={loading}
            className="hidden"
          />
          <Button 
            onClick={() => fileInputRef.current.click()}
            className="px-4 py-2 flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
            disabled={loading}
          >
            <UploadCloud className="h-4 w-4" />
            Ajouter un fichier
          </Button>
        </div>
      )}
      
      <div 
        ref={dropZoneRef}
        className={`flex-1 overflow-y-auto relative ${!isSimplified && isDragging ? 'bg-blue-50 dark:bg-blue-900/20 border-2 border-dashed border-blue-400 dark:border-blue-600 rounded-lg' : ''}`}
      >
        {!isSimplified && isDragging && (
          <div className="absolute inset-0 flex items-center justify-center bg-blue-50/80 dark:bg-blue-900/50 z-10 pointer-events-none">
            <div className="text-center">
              <UploadCloud className="h-12 w-12 mx-auto mb-2 text-blue-500" />
              <p className="text-blue-600 dark:text-blue-400 font-medium">Déposez votre fichier ici</p>
            </div>
          </div>
        )}
        
        {/* Section des assets du projet */}
        {!isSimplified && (
          <h4 className="font-medium text-sm px-2 mt-2 mb-1">Mes Assets</h4>
        )}
        <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 p-2">
          {assets.map(asset => renderAsset(asset))}
          
          {assets.length === 0 && (
            <div className="col-span-full flex flex-col items-center justify-center p-6 text-center text-gray-500">
              <FileImage className="h-12 w-12 mb-2 opacity-50" />
              <p>Aucun asset disponible</p>
              {!isSimplified && (
                <>
                  <p className="text-xs">Téléchargez votre premier fichier</p>
                  <p className="text-xs mt-2">Ou glissez-déposez des fichiers ici</p>
                </>
              )}
            </div>
          )}
        </div>
        
        {/* Section des assets partagés - masquée en mode simplifié */}
        {!isSimplified && sharedAssets.length > 0 && (
          <>
            <h4 className="font-medium text-sm px-2 mt-4 mb-1 flex items-center">
              <Share2 className="h-4 w-4 mr-1 text-blue-500" /> 
              Assets Partagés
            </h4>
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 p-2 border-t pt-2">
              {sharedAssets.map(asset => renderAsset(asset, true))}
            </div>
          </>
        )}
      </div>

      {/* Dialog de suppression - masqué en mode simplifié */}
      {!isSimplified && (
        <AlertDialog open={showDeleteAlert} onOpenChange={setShowDeleteAlert}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Êtes-vous sûr?</AlertDialogTitle>
              <AlertDialogDescription>
                Cette action supprimera définitivement l'asset "{assetToDelete?.name}".
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDeleteAsset} 
                className="bg-red-500 hover:bg-red-600"
                disabled={loading}
              >
                {loading ? 'Suppression...' : 'Supprimer'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
};

export default AssetManager; 